import { Center, Flex, Select, Stack, Tabs, Title } from "@mantine/core";
import { DatePicker, type DatePickerProps } from "@mantine/dates";
import { IconCalendar, IconList } from "@tabler/icons-react";
import * as df from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { client } from "@/api-client";
import type { SessionWithOwnerDogDisplayListOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { SessionCard } from "@/components/SessionCard";
import { isSessionCancelled } from "@/components/SessionUtils";

export type SessionList = SessionWithOwnerDogDisplayListOutput[];

export const SessionListContent = ({
  sessionList,
  onlyListView,
}: {
  sessionList: SessionList;
  onlyListView?: boolean;
}) => {
  const [filter, setFilter] = useState<string>("all");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [filterType, setFilterType] = useState<"filter" | "calendar">("filter");

  const filterSessions = (sessions: SessionList) => {
    const filteredSessions = (() => {
      if (filterType === "calendar") {
        if (selectedDate == null) {
          return [];
        }
        // Filter sessions for selected date
        return sessions.filter((session) => {
          if (isSessionCancelled(session.status)) return false;
          if (!session.scheduledAt) return false;
          const sessionDate = df.parseISO(session.scheduledAt);
          return df.isSameDay(sessionDate, selectedDate);
        });
      }

      if (filter === "requested") {
        return sessions.filter((session) => session.status === "REQUESTED");
      }
      if (filter === "future") {
        return sessions.filter((session) => session.status === "CONFIRMED");
      }
      if (filter === "past") {
        return sessions.filter((session) => session.status === "COMPLETED");
      }
      if (filter === "cancelled") {
        return sessions.filter((session) => isSessionCancelled(session.status));
      }
      if (filter === "all") {
        return sessions.filter(
          (session) =>
            session.status === "REQUESTED" ||
            session.status === "CONFIRMED" ||
            session.status === "COMPLETED",
        );
      }

      return [];
    })();

    return filteredSessions;
  };

  const filteredSessions = filterSessions(sessionList);

  const scheduledDates = useMemo(
    () =>
      sessionList
        .filter(
          (session): session is typeof session & { scheduledAt: string } =>
            !!session.scheduledAt && !isSessionCancelled(session.status),
        )
        .map((session) => df.parseISO(session.scheduledAt)),
    [sessionList],
  );

  const renderDate: DatePickerProps["renderDay"] = useCallback(
    (dateStr: string) => {
      const date = new Date(dateStr);
      const day = date.getDate();
      const hasSessions = scheduledDates.some((slot) =>
        df.isSameDay(slot, date),
      );
      if (hasSessions) {
        return (
          <Flex
            w="100%"
            h="100%"
            justify="center"
            align="center"
            bd="1px solid blue"
            style={{ borderRadius: "5px" }}
          >
            <div>{day}</div>
          </Flex>
        );
      }
      return <div>{day}</div>;
    },
    [scheduledDates],
  );

  const selectFilter = (
    <Select
      mt="md"
      value={filter}
      // biome-ignore lint/style/noNonNullAssertion: the value is always there
      onChange={(value) => setFilter(value!)}
      allowDeselect={false}
      data={[
        { value: "all", label: "All Sessions" },
        { value: "requested", label: "Requires Confirmation" },
        { value: "future", label: "Future Sessions" },
        { value: "past", label: "Past Sessions" },
        { value: "cancelled", label: "Cancelled Sessions" },
      ]}
    />
  );

  return (
    <Stack>
      {onlyListView ? (
        selectFilter
      ) : (
        <Tabs
          defaultValue="filter"
          value={filterType}
          onChange={(value) => setFilterType(value as "filter" | "calendar")}
        >
          <Tabs.List grow justify="center">
            <Tabs.Tab value="filter" leftSection={<IconList size={14} />}>
              List View
            </Tabs.Tab>
            <Tabs.Tab value="calendar" leftSection={<IconCalendar size={14} />}>
              Calendar View
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="filter">{selectFilter}</Tabs.Panel>

          <Tabs.Panel value="calendar">
            <Center mt="md">
              <DatePicker
                value={selectedDate}
                onChange={(value) => {
                  if (typeof value === "string") {
                    setSelectedDate(value ? new Date(value) : null);
                  } else {
                    setSelectedDate(value);
                  }
                }}
                renderDay={renderDate}
              />
            </Center>
          </Tabs.Panel>
        </Tabs>
      )}

      {filteredSessions.map((session) => (
        <SessionCard key={session.id} session={session} who="trainer" />
      ))}
    </Stack>
  );
};

export const SessionList = () => {
  const { data: sessionList, isLoading } = client.useQuery(
    "get",
    "/trainer/sessions/",
  );
  let content = <FancyLoader />;
  if (sessionList && !isLoading) {
    content = <SessionListContent sessionList={sessionList.sessions} />;
  }

  return (
    <>
      <Title order={1}>Sessions</Title>
      {content}
    </>
  );
};
